// 📁 src/components/MessageBubble.jsx - VERSIÓN CORREGIDA FINAL
import React, { useState } from 'react';
import clsx from 'clsx';
import { marked } from 'marked';
import * as LucideIcons from 'lucide-react';
import FeedbackBubble from "./FeedbackBubble";
import ResultadosSimuladorComponent from "./ResultadosSimuladorComponent";
import DiagnosticFeedbackBubble from "./DiagnosticFeedbackBubble";
import WelcomeMenuBubble from "./WelcomeMenuBubble";
import VoiceButton from "./VoiceButton";
import MicButton from "./MicButton";
// 🚀 EPL - Componentes reutilizables para las 9 tarjetas
import { renderEPLMessage, EPL_VISUAL_TYPES } from '../utils/renderMessageHelper.jsx';

// Componente para el ícono
const Icon = ({ name, size = 16, className = "" }) => {
    const LucideIcon = LucideIcons[name];
    return LucideIcon ? <LucideIcon size={size} className={clsx("stroke-[1.5]", className)} /> : null;
};

// 🤖 Loading skeleton mejorado según sugerencias del experto
const AILoadingSkeleton = ({ type = "single-shot" }) => (
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
        <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-indigo-200 rounded-full animate-pulse flex items-center justify-center">
                <Icon name="Brain" className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-indigo-700">Coach IA analizando</span>
                <div className="flex items-center space-x-1">
                    <span className="h-1 w-1 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                    <span className="h-1 w-1 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                    <span className="h-1 w-1 bg-indigo-400 rounded-full animate-bounce"></span>
                </div>
            </div>
        </div>
        
        <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2"></div>
        </div>
        
        {type === "single-shot" && (
            <div className="mt-4 text-xs text-indigo-600">
                📊 Generando análisis personalizado...
            </div>
        )}
        
        {type === "sparring" && (
            <div className="mt-4 text-xs text-indigo-600">
                🎭 Preparando simulación de entrevista...
            </div>
        )}
        
        {type === "analista" && (
            <div className="mt-4 text-xs text-indigo-600">
                📈 Procesando métricas y tendencias...
            </div>
        )}
    </div>
);

// 🏷️ Chip "Generado por IA" con rol y fuente específicos según experto  
const AIGeneratedChip = ({ type = "single-shot", limit = null, current = null, role = null, source = null }) => (
    <div className="flex items-center gap-2 mb-3">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-3 py-1 rounded-full text-xs font-medium shadow-md">
            <Icon name="Brain" className="w-3 h-3" />
            <span>IA {role ? `- ${role.toUpperCase()}` : 'Generada'}</span>
        </div>
        
        {type === "sparring" && limit && current && (
            <div className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">
                <Icon name="MessageCircle" className="w-3 h-3" />
                <span>Turno {current}/{limit}</span>
            </div>
        )}
        
        {(type === "single-shot" || type === "tutor") && (
            <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                <Icon name="GraduationCap" className="w-3 h-3" />
                <span>Tutor IA</span>
            </div>
        )}
        
        {type === "analista" && (
            <div className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                <Icon name="TrendingUp" className="w-3 h-3" />
                <span>Analista IA</span>
            </div>
        )}
        
        {type === "evaluador" && (
            <div className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium">
                <Icon name="Award" className="w-3 h-3" />
                <span>Evaluador IA</span>
            </div>
        )}
        
        {/* 🔄 Indicador de fuente IA */}
        {source && (
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                source === 'openai' 
                    ? 'bg-emerald-100 text-emerald-700'
                    : source === 'fallback' 
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-gray-100 text-gray-700'
            }`}>
                <Icon name={source === 'openai' ? 'Zap' : 'Wifi'} className="w-3 h-3" />
                <span>{source === 'openai' ? 'OpenAI' : source === 'fallback' ? 'Offline' : 'Local'}</span>
            </div>
        )}
    </div>
);

const MessageBubble = ({ message, onOptionClick, onInputSubmit, showInput, isProcessing, selectedValue = null }) => {
    console.log("[MessageBubble] Recibió el siguiente objeto de mensaje:", message);
    if (message && typeof message.content === 'object' && message.content !== null) {
        console.log(` -> Mensaje con contenido OBJETO. Su tipo_visual es: [${message.tipo_visual}]`);
    }

    if (!message) {
        console.error("MessageBubble recibió un 'message' prop undefined.");
        return null;
    }

    // 🤖 Loading skeleton mejorado según sugerencias del experto
    if (message.isLoading || message.tipo_visual === 'ai_loading') {
        return (
            <div className="flex items-start mb-6">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center">
                    <Icon name="Brain" className="w-4 h-4 text-white animate-pulse" />
                </div>
                <div className="ml-3 flex-1">
                    <AILoadingSkeleton type={message.ai_type || "single-shot"} />
                </div>
            </div>
        );
    }

    if (message.isTyping) {
        return (
            <div className="flex items-start mb-6">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
                    <Icon name="BrainCircuit" />
                </div>
                <div className="ml-3 bg-gray-200 rounded-2xl rounded-bl-none">
                    <TypingIndicator />
                </div>
            </div>
        );
    }

    const { sender, tipo_visual, opciones } = message;
    const content = message.content || message.mensaje_coach?.texto;
    const titulo = message.titulo || message.mensaje_coach?.titulo;
    const isUser = sender === 'user';

    // --- Delegación por tipo_visual ---
	console.log("[🔍 MessageBubble] tipo_visual:", tipo_visual);
	console.log("[🔍 MessageBubble] titulo:", message.titulo);
	console.log("[🔍 MessageBubble] mensaje_coach:", message.mensaje_coach);
	
	// 🚀 NUEVOS TIPOS EPL - Reutilizables para las 9 tarjetas
	// Verificar si es un tipo visual EPL
	if (EPL_VISUAL_TYPES[tipo_visual]) {
	    console.log(`[🎯 EPL] Renderizando tipo visual: ${tipo_visual}`);
	    const eplComponent = renderEPLMessage(message, onOptionClick, null, isProcessing, selectedValue);
	    if (eplComponent) {
	        return eplComponent;
	    }
	}
	
    // 🎭 IA Sparring acotado según sugerencias del experto
    if (tipo_visual === 'sparring_chat') {
        return <SparringChatWidget {...{ message, onOptionClick, onInputSubmit, isProcessing }} />;
    }

    // 🔄 TIPOS EXISTENTES - Mantener compatibilidad
    if (tipo_visual === 'bienvenida' || (titulo && opciones?.length >= 1 && tipo_visual !== 'formulario_innovatech')) {
        return <WelcomeMenuBubble {...{ message, onOptionClick, isProcessing }} />;
    }    if (tipo_visual === 'feedback_estrategico' || tipo_visual === 'feedback_basico') {
        return <FeedbackBubble {...{ content, opciones, onOptionClick, isProcessing }} />;
    }

    if (tipo_visual === 'feedback_diagnostico') {
        return <DiagnosticFeedbackBubble {...{ content, opciones, onOptionClick, isProcessing }} />;
    }

    if (tipo_visual === 'resultados_simulador') {
        return <ResultadosSimuladorComponent {...{ content, opciones, onOptionClick, isProcessing }} />;
    }

    // --- Render genérico de contenido (última línea de defensa) ---
    const renderContent = () => {
        if (typeof content === 'string') {
            return <div className="prose prose-sm max-w-none break-words" dangerouslySetInnerHTML={{ __html: marked.parse(content || "") }} />;
        }

        if (typeof content === 'object' && content !== null) {
            const { titulo = '', texto = '', respuestaCorrecta = '', bullets = [] } = {
                ...content,
                titulo: content.titulo || message.mensaje_coach?.titulo,
                texto: content.texto || message.mensaje_coach?.texto
            };
            const partes = [titulo, texto, respuestaCorrecta, ...bullets].filter(Boolean).join("\n\n");

            return (
                <div className="prose prose-sm max-w-none whitespace-pre-wrap break-words">
                    {partes}
                </div>
            );
        }

        return <p>{String(content)}</p>;
    };

    return (
        <div className={clsx("flex w-full mb-6", isUser ? 'justify-end' : 'justify-start')}>
            <div className={clsx("flex gap-3 w-[90%] sm:max-w-[85%]", isUser ? 'flex-row-reverse' : 'flex-row')}>
                {!isUser && (
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
                        <Icon name="BrainCircuit" />
                    </div>
                )}

                <div className={clsx(
                    'rounded-2xl px-4 py-3 shadow-md w-full',
                    isUser ? 'bg-blue-500 text-white rounded-br-none' : 'bg-white text-gray-800 rounded-bl-none'
                )}>
                    {/* 🤖 Chip "Generado por IA" con rol y fuente específicos según experto */}
                    {!isUser && message.generated_by_ai && (
                        <AIGeneratedChip 
                            type={message.ai_type || "single-shot"}
                            role={message.ai_role}
                            source={message.ai_source}
                            limit={message.ai_limit}
                            current={message.ai_current}
                        />
                    )}
                    
                    {renderContent()}

                    {/* � Botón de voz - Solo para mensajes del agente */}
                    {!isUser && typeof content === 'string' && content.trim() && (
                        <VoiceButton text={content} />
                    )}

                    {/* �🎨 FORMULARIO MEJORADO PARA INNOVATECH */}
                    {(showInput || message.mostrar_input_text) && (
                        <InnovaTechForm 
                            onInputSubmit={onInputSubmit}
                            placeholder={message.placeholders?.input_hint || "Escribe tu respuesta aquí..."}
                            isProcessing={isProcessing}
                            opciones={opciones}
                            onOptionClick={onOptionClick}
                            inputValue={message.inputValue}
                            onInputChange={message.onInputChange}
                        />
                    )}

                    {/* 🔘 RADIO BUTTONS NORMALES - Solo cuando NO hay formulario */}
                    {!(showInput || message.mostrar_input_text) && opciones && opciones.length > 0 && (
                        <div className="flex flex-col gap-2 mt-4">
                            {opciones.map((opcion, index) => (
                                <button
                                    key={index}
                                    onClick={() => onOptionClick(opcion)}
                                    disabled={isProcessing}
                                    className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-left text-sm font-medium text-gray-800 bg-gray-50 hover:bg-gray-100 shadow-sm disabled:opacity-50"
                                >
                                    <Icon name={opcion.icono || 'ChevronRight'} className="w-4 h-4 mr-2 flex-shrink-0" />
                                    <span className="flex-1">{opcion.etiqueta}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// 🎨 Componente mejorado para formularios InnovaTech con UX optimizada
const InnovaTechForm = ({ onInputSubmit, placeholder, isProcessing, opciones, onOptionClick, inputValue, onInputChange }) => {
    
    const handleOptionClick = (option) => {
        const needsInput = ['enviar_respuesta', 'save_hypothesis'].includes(option.valor);
        
        if (needsInput) {
            // Verificar si hay texto antes de proceder
            if (!inputValue || inputValue.trim() === '') {
                alert('Por favor, escribe tu hipótesis antes de continuar.');
                return;
            }
            // Llamar directamente a onInputSubmit con el texto
            onInputSubmit(inputValue, option.valor);
        } else {
            // Para "Ver ejemplo" y otros que no necesitan input
            onOptionClick(option);
        }
    };

    return (
        <div className="mt-8 bg-gradient-to-br from-slate-50 via-white to-blue-50 rounded-3xl p-8 border-2 border-slate-200 shadow-xl relative overflow-hidden">
            {/* Patrón de fondo sutil */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100/30 to-indigo-100/30 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-purple-100/30 to-pink-100/30 rounded-full blur-2xl"></div>
            
            {/* Contenido con z-index superior */}
            <div className="relative z-10">
            
            {/* 🎯 HEADER PREMIUM DEL FORMULARIO */}
            <div className="text-center mb-8">
                <div className="inline-flex items-center gap-3 mb-4 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-lg">
                    <span className="text-2xl animate-bounce">💡</span>
                    <h3 className="text-xl font-bold text-white">
                        Formulario de Hipótesis InnovaTech
                    </h3>
                    <span className="text-2xl animate-bounce animation-delay-150">🚀</span>
                </div>
                <p className="text-gray-600 max-w-lg mx-auto leading-relaxed">
                    Desarrolla tu hipótesis de innovación con el apoyo de nuestro Coach IA especializado
                </p>
            </div>
            
            {/* 📝 TEXTAREA PREMIUM CON TEMPLATE DEL EXPERTO */}
            <div className="mb-8 bg-white/70 rounded-2xl p-6 backdrop-blur-sm border border-white/50 shadow-lg">
                <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="text-lg">📝</span>
                    Describe tu hipótesis usando el template estructurado
                </label>
                
                {/* 🎯 Template según especificaciones del experto */}
                <div className="mb-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
                    <div className="text-sm font-medium text-indigo-800 mb-2 flex items-center gap-2">
                        <Icon name="Template" className="w-4 h-4" />
                        Template Obligatorio:
                    </div>
                    <div className="text-sm text-indigo-700 font-mono bg-white/60 p-3 rounded-lg">
                        <span className="font-bold">Creemos que</span> [grupo de clientes] 
                        <span className="font-bold"> tienen</span> [problema] 
                        <span className="font-bold"> porque</span> [razón observable]
                    </div>
                    <div className="text-xs text-indigo-600 mt-2">
                        ✅ Ejemplo: "Creemos que <em>estudiantes universitarios de ingeniería</em> tienen <em>dificultades para encontrar prácticas relevantes</em> porque <em>las plataformas actuales no filtran por especialización técnica</em>."
                    </div>
                </div>
                
                <div className="relative group">
                    {/* 🎯 Validación del template en tiempo real */}
                    {inputValue && (
                        <TemplateValidator text={inputValue} />
                    )}
                    
                    <textarea
                        value={inputValue || ''}
                        onChange={(e) => onInputChange(e.target.value)}
                        placeholder="Creemos que [tu grupo de clientes] tienen [problema específico] porque [razón que puedes observar]..."
                        className="w-full h-40 p-6 border-2 border-gray-300 rounded-2xl resize-none 
                                 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 
                                 group-hover:border-gray-400 transition-all duration-300 
                                 bg-gradient-to-br from-white to-gray-50 shadow-inner
                                 text-gray-800 placeholder-gray-400 text-base leading-relaxed
                                 backdrop-blur-sm"
                        disabled={isProcessing}
                        maxLength={500}
                    />
                    
                    {/* Indicador de estado visual mejorado */}
                    <div className="absolute top-4 right-4 flex items-center gap-2">
                        {inputValue && inputValue.length > 0 ? (
                            <div className="flex items-center gap-2 bg-green-100 px-3 py-1 rounded-full border border-green-200">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-xs font-medium text-green-700">Activo</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full border border-gray-200">
                                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                <span className="text-xs font-medium text-gray-600">Esperando...</span>
                            </div>
                        )}
                    </div>
                    
                    {/* Barra de progreso visual */}
                    <div className="absolute bottom-2 right-2 left-2">
                        <div className="bg-gray-200 rounded-full h-1 overflow-hidden">
                            <div 
                                className="h-full bg-gradient-to-r from-blue-400 to-indigo-500 transition-all duration-300 ease-out"
                                style={{ width: `${Math.min(100, ((inputValue || '').length / 500) * 100)}%` }}
                            ></div>
                        </div>
                    </div>
                </div>
                
                <div className="flex justify-between items-center mt-4 px-2">
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-600">
                            {(inputValue || '').length}<span className="text-gray-400 ml-1">/500</span>
                        </span>
                        
                        {(inputValue || '').length > 400 && (
                            <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full border border-amber-200">
                                <span>⚠️</span>
                                <span>Cerca del límite</span>
                            </div>
                        )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                        {/* Botón de micrófono para dictar hipótesis */}
                        <MicButton 
                          onTranscriptionComplete={(text) => {
                            onInputChange(inputValue ? `${inputValue} ${text}` : text);
                          }}
                          disabled={isProcessing}
                          className="text-xs"
                        />
                        
                        {inputValue && inputValue.length > 20 && (
                            <div className="flex items-center gap-2 text-xs font-medium text-emerald-700 bg-gradient-to-r from-emerald-50 to-green-50 px-3 py-2 rounded-full border border-emerald-200 shadow-sm">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce"></div>
                                ¡Excelente! Listo para analizar
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 🎨 RADIO BUTTONS CON DISEÑO PREMIUM */}
            <div className="space-y-4 max-w-2xl mx-auto">
                {opciones?.map((opcion, index) => {
                    // Determinar estilo según tipo de acción
                    const isSubmit = opcion.valor === 'enviar_respuesta';
                    const isSave = opcion.valor === 'save_hypothesis'; 
                    const isHelper = opcion.valor.includes('ejemplo');
                    
                    let radioStyle = 'group relative flex items-center p-5 rounded-2xl border-2 cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg ';
                    let iconColor = 'text-gray-400 group-hover:text-gray-600';
                    let textColor = 'text-gray-700 group-hover:text-gray-900';
                    let descColor = 'text-gray-500';
                    let glowClass = '';
                    
                    if (isSubmit) {
                        radioStyle += 'border-green-300 bg-gradient-to-r from-green-50 to-green-100 hover:border-green-400 hover:from-green-100 hover:to-green-200';
                        iconColor = 'text-green-700';
                        textColor = 'text-green-900 font-semibold';
                        descColor = 'text-green-700';
                        glowClass = 'group-hover:shadow-green-200';
                    } else if (isSave) {
                        radioStyle += 'border-blue-300 bg-gradient-to-r from-blue-50 to-blue-100 hover:border-blue-400 hover:from-blue-100 hover:to-blue-200';
                        iconColor = 'text-blue-700';
                        textColor = 'text-blue-900 font-semibold';
                        descColor = 'text-blue-700';
                        glowClass = 'group-hover:shadow-blue-200';
                    } else if (isHelper) {
                        radioStyle += 'border-orange-300 bg-gradient-to-r from-orange-50 to-orange-100 hover:border-orange-400 hover:from-orange-100 hover:to-orange-200';
                        iconColor = 'text-orange-700';
                        textColor = 'text-orange-900 font-semibold';
                        descColor = 'text-orange-700';
                        glowClass = 'group-hover:shadow-orange-200';
                    } else {
                        radioStyle += 'border-gray-300 bg-gradient-to-r from-gray-50 to-gray-100 hover:border-gray-400 hover:from-gray-100 hover:to-gray-200';
                    }
                    
                    return (
                        <div
                            key={index}
                            onClick={() => handleOptionClick(opcion)}
                            className={`${radioStyle} ${glowClass}` + (isProcessing ? ' opacity-50 cursor-not-allowed' : '')}
                        >
                            {/* ✨ RADIO CIRCLE PREMIUM CON ANIMACIÓN */}
                            <div className={`relative w-6 h-6 rounded-full border-3 mr-5 flex items-center justify-center transition-all duration-200 ${
                                isSubmit ? 'border-green-500 shadow-md shadow-green-200' : 
                                isSave ? 'border-blue-500 shadow-md shadow-blue-200' :
                                isHelper ? 'border-orange-500 shadow-md shadow-orange-200' : 'border-gray-400'
                            }`}>
                                <div className={`w-3 h-3 rounded-full transition-all duration-200 ${
                                    isSubmit ? 'bg-green-600 group-hover:scale-110 group-hover:animate-pulse' : 
                                    isSave ? 'bg-blue-600 group-hover:scale-110 group-hover:animate-pulse' :
                                    isHelper ? 'bg-orange-600 group-hover:scale-110 group-hover:animate-pulse' : 'bg-gray-500'
                                } opacity-0 group-hover:opacity-100`}></div>
                                {/* Anillo de resplandor en hover */}
                                <div className={`absolute -inset-1 rounded-full opacity-0 group-hover:opacity-30 transition-all duration-200 ${
                                    isSubmit ? 'bg-green-400 group-hover:animate-ping' :
                                    isSave ? 'bg-blue-400 group-hover:animate-ping' :
                                    isHelper ? 'bg-orange-400 group-hover:animate-ping' : 'bg-gray-400'
                                }`}></div>
                            </div>
                            
                            {/* 🎯 ICONO PREMIUM CON EFECTOS */}
                            <div className="relative mr-4">
                                <Icon 
                                    name={opcion.icono || 'circle'} 
                                    className={`w-6 h-6 transition-all duration-200 group-hover:scale-110 ${iconColor}`}
                                />
                                {/* Emoji de contexto */}
                                <span className="absolute -top-1 -right-1 text-sm">
                                    {isSubmit && '🚀'}
                                    {isSave && '💾'}
                                    {isHelper && '💡'}
                                </span>
                            </div>
                            
                            {/* 📝 CONTENIDO MEJORADO */}
                            <div className="flex-1 min-w-0">
                                <div className={`text-base font-semibold ${textColor} mb-2 transition-colors duration-200`}>
                                    {opcion.etiqueta}
                                </div>
                                <div className={`text-sm leading-relaxed ${descColor} transition-colors duration-200`}>
                                    {isSubmit && '🎯 Obtener análisis inteligente del Coach IA especializado'}
                                    {isSave && '📂 Guardar progreso y continuar al siguiente paso automáticamente'}
                                    {isHelper && '🔍 Ver ejemplo pedagógico detallado y estructurado'}
                                </div>
                            </div>
                            
                            {/* 🚨 INDICADOR DE ESTADO MEJORADO */}
                            {(isSubmit || isSave) && (!inputValue || inputValue.trim() === '') && (
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
                                    <div className="text-xs font-medium text-amber-700 bg-gradient-to-r from-amber-50 to-amber-100 px-3 py-2 rounded-full border border-amber-200">
                                        Requiere hipótesis
                                    </div>
                                </div>
                            )}
                            
                            {/* ✅ INDICADOR DE DISPONIBLE */}
                            {(isSubmit || isSave) && inputValue && inputValue.trim() !== '' && (
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce"></div>
                                    <div className="text-xs font-medium text-green-700 bg-gradient-to-r from-green-50 to-green-100 px-3 py-2 rounded-full border border-green-200">
                                        ¡Listo!
                                    </div>
                                </div>
                            )}
                            
                            {/* 💡 SIEMPRE DISPONIBLE PARA EJEMPLOS */}
                            {isHelper && (
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                                    <div className="text-xs font-medium text-orange-700 bg-gradient-to-r from-orange-50 to-orange-100 px-3 py-2 rounded-full border border-orange-200">
                                        Disponible
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            </div> {/* Cierre del contenido con z-index */}
        </div>
    );
};

// 🎭 Widget de IA Sparring acotado según sugerencias del experto
const SparringChatWidget = ({ message, onOptionClick, onInputSubmit, isProcessing }) => {
    const [inputValue, setInputValue] = useState('');
    const { sparring_profile, ai_limit, ai_current, mensaje_coach, opciones, placeholder } = message;
    
    const profileConfig = {
        cliente_esceptico: {
            name: "Cliente Escéptico",
            avatar: "😤",
            color: "from-red-500 to-orange-500",
            description: "Cuestiona todo y busca pruebas"
        },
        cliente_entusiasta: {
            name: "Cliente Entusiasta", 
            avatar: "🤩",
            color: "from-green-500 to-blue-500",
            description: "Muy interesado pero tiene dudas técnicas"
        },
        cliente_desinteresado: {
            name: "Cliente Desinteresado",
            avatar: "😐", 
            color: "from-gray-500 to-slate-500",
            description: "Difícil de convencer, necesita valor claro"
        }
    };
    
    const profile = profileConfig[sparring_profile] || profileConfig.cliente_esceptico;
    const progressPercent = ((ai_current || 1) / (ai_limit || 5)) * 100;
    
    const handleSparringSubmit = (input) => {
        if (onInputSubmit) {
            onInputSubmit(input, 'sparring_response');
            setInputValue('');
        }
    };

    return (
        <div className="max-w-2xl mx-auto bg-gradient-to-br from-purple-50 via-white to-indigo-50 rounded-3xl p-6 border-2 border-purple-200 shadow-lg">
            {/* 🎭 Header del perfil simulado */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${profile.color} flex items-center justify-center text-2xl shadow-lg`}>
                        {profile.avatar}
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-800">{profile.name}</h3>
                        <p className="text-sm text-gray-600">{profile.description}</p>
                    </div>
                </div>
                
                {/* Contador de turnos */}
                <div className="text-right">
                    <div className="text-sm font-medium text-purple-700">
                        Turno {ai_current || 1}/{ai_limit || 5}
                    </div>
                    <div className="w-24 h-2 bg-purple-200 rounded-full overflow-hidden mt-1">
                        <div 
                            className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all duration-300"
                            style={{ width: `${progressPercent}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            {/* 🤖 Chip generado por IA */}
            <AIGeneratedChip 
                type="sparring" 
                limit={ai_limit} 
                current={ai_current}
            />

            {/* 💬 Contenido del mensaje */}
            <div className="bg-white/70 rounded-2xl p-5 mb-6 backdrop-blur-sm border border-white/50 shadow-inner">
                <div className="prose prose-sm max-w-none" 
                     dangerouslySetInnerHTML={{ __html: marked.parse(mensaje_coach?.cuerpo || '') }} />
            </div>

            {/* ⌨️ Input de respuesta */}
            <div className="mb-6">
                <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={placeholder || "Escribe tu respuesta..."}
                    className="w-full h-24 p-4 border-2 border-purple-300 rounded-xl resize-none 
                             focus:ring-3 focus:ring-purple-500/30 focus:border-purple-500 
                             transition-all duration-200 bg-white/80 backdrop-blur-sm
                             text-gray-800 placeholder-gray-400"
                    disabled={isProcessing}
                />
                
                <div className="flex justify-between items-center mt-3">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                            {inputValue.length}/200 caracteres
                        </span>
                        
                        {/* Botón de micrófono para respuestas de sparring */}
                        <MicButton 
                          onTranscriptionComplete={(text) => {
                            setInputValue(prev => prev ? `${prev} ${text}` : text);
                          }}
                          disabled={isProcessing}
                          className="text-xs"
                        />
                    </div>
                    
                    {inputValue.length > 10 && (
                        <button
                            onClick={() => handleSparringSubmit(inputValue)}
                            disabled={isProcessing}
                            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 
                                     text-white rounded-lg font-medium hover:from-purple-600 hover:to-indigo-600
                                     disabled:opacity-50 transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                            Responder
                        </button>
                    )}
                </div>
            </div>

            {/* 🎯 Botones de acción */}
            <div className="flex flex-wrap gap-3">
                {opciones?.map((opcion, index) => {
                    const isEnd = opcion.valor === 'end_sparring';
                    const colorClass = isEnd 
                        ? 'border-red-300 bg-red-50 hover:bg-red-100 text-red-700'
                        : 'border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-700';
                        
                    return (
                        <button
                            key={index}
                            onClick={() => onOptionClick(opcion)}
                            disabled={isProcessing}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 font-medium
                                       transition-all duration-200 hover:scale-105 disabled:opacity-50 ${colorClass}`}
                        >
                            <Icon name={opcion.icono || 'Circle'} className="w-4 h-4" />
                            {opcion.etiqueta}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

// 🎯 Validador de template en tiempo real según especificaciones del experto
const TemplateValidator = ({ text }) => {
    const template = "Creemos que [grupo de clientes] tienen [problema] porque [razón observable]";
    
    const hasCreemos = text.toLowerCase().includes('creemos que') || text.toLowerCase().includes('creo que');
    const hasTienen = text.toLowerCase().includes('tienen') || text.toLowerCase().includes('tiene');
    const hasPorque = text.toLowerCase().includes('porque') || text.toLowerCase().includes('ya que');
    
    const validations = [
        { check: hasCreemos, text: "✅ Inicia con 'Creemos que'", error: "❌ Debe iniciar con 'Creemos que'" },
        { check: hasTienen, text: "✅ Especifica que 'tienen' problema", error: "❌ Falta especificar que el grupo 'tienen' un problema" },
        { check: hasPorque, text: "✅ Explica 'porque' razón", error: "❌ Falta explicar 'porque' [razón observable]" }
    ];
    
    const passedChecks = validations.filter(v => v.check).length;
    const isComplete = passedChecks === 3;
    
    return (
        <div className="absolute -top-12 left-0 right-0 z-10">
            <div className={`p-3 rounded-xl text-xs shadow-lg border ${
                isComplete 
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-amber-50 border-amber-200 text-amber-800'
            }`}>
                <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">
                        {isComplete ? '🎯 Template Completo' : `📝 Progreso: ${passedChecks}/3`}
                    </span>
                    <div className="flex gap-1">
                        {validations.map((val, i) => (
                            <div key={i} className={`w-2 h-2 rounded-full ${val.check ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        ))}
                    </div>
                </div>
                
                <div className="space-y-1">
                    {validations.map((val, i) => (
                        <div key={i} className="text-xs">
                            {val.check ? val.text : val.error}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MessageBubble;
